import React, { useRef, useEffect, useState, useCallback } from 'react'
import { MapPin, Loader, AlertCircle } from 'lucide-react'
import toast from 'react-hot-toast'
import { api } from '@/lib/api'

interface PlacePrediction {
  place_id: string
  description: string
  main_text: string
  secondary_text?: string
}

interface GooglePlacesAutocompleteProps {
  value: string
  onChange: (value: string) => void
  onAddressSelect?: (details: AddressDetails) => void
  placeholder?: string
  disabled?: boolean
  error?: string
}

export interface AddressDetails {
  formattedAddress: string
  address?: string
  city?: string
  district?: string
  country?: string
  latitude?: number
  longitude?: number
  placeId?: string
}

/**
 * Google Places Autocomplete component for address search
 * Provides real-time suggestions and geocoding capabilities
 */
export function GooglePlacesAutocomplete({
  value,
  onChange,
  onAddressSelect,
  placeholder = 'Enter address...',
  disabled = false,
  error
}: GooglePlacesAutocompleteProps) {
  const [predictions, setPredictions] = useState<PlacePrediction[]>([])
  const [isOpen, setIsOpen] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [sessionToken] = useState(() => Math.random().toString(36).substring(7))
  const containerRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const debounceTimerRef = useRef<number>()

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  // Fetch predictions with debouncing
  const fetchPredictions = useCallback(async (input: string) => {
    if (!input || input.length < 2) {
      setPredictions([])
      return
    }

    setIsLoading(true)
    try {
      const response = await api.get('/geocoding/autocomplete', {
        params: {
          input,
          sessionToken,
          region: 'pt' // Portugal region
        }
      })

      if (response.data.success && response.data.predictions) {
        console.log('Received predictions:', response.data.predictions)
        setPredictions(response.data.predictions)
        setIsOpen(true)
      } else {
        console.log('No predictions received:', response.data)
        setPredictions([])
      }
    } catch (error) {
      console.error('Error fetching predictions:', error)
      toast.error('Failed to fetch address suggestions')
    } finally {
      setIsLoading(false)
    }
  }, [sessionToken])

  // Handle input change with debounce
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value
    onChange(newValue)

    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current)
    }

    debounceTimerRef.current = setTimeout(() => {
      fetchPredictions(newValue)
    }, 300)
  }

  // Debug logging
  useEffect(() => {
    console.log('GooglePlacesAutocomplete Debug:', {
      isOpen,
      predictionsCount: predictions.length,
      isLoading,
      value
    })
  }, [isOpen, predictions.length, isLoading, value])

  // Handle place selection
  const handleSelectPlace = async (prediction: PlacePrediction) => {
    setIsOpen(false)
    setIsLoading(true)

    try {
      // Get place details including coordinates
      const response = await api.get('/geocoding/place-details', {
        params: {
          placeId: prediction.place_id,
          sessionToken
        }
      })

      if (response.data.success) {
        const details = response.data.details as AddressDetails
        
        // Update input with formatted address
        onChange(details.formattedAddress || prediction.description)
        
        // Call callback with full details for parent component
        if (onAddressSelect) {
          onAddressSelect(details)
        }

        toast.success('Address geocoded successfully!')
      } else {
        toast.error('Failed to get address details')
      }
    } catch (error) {
      console.error('Error fetching place details:', error)
      toast.error('Failed to fetch address details')
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div ref={containerRef} className="relative w-full">
      <div className="relative">
        <div className="absolute left-3 top-1/2 -translate-y-1/2 text-secondary-500">
          {isLoading ? (
            <Loader className="h-4 w-4 animate-spin" />
          ) : (
            <MapPin className="h-4 w-4" />
          )}
        </div>

        <input
          ref={inputRef}
          type="text"
          value={value}
          onChange={handleInputChange}
          onFocus={() => {
            console.log('Input focused, predictions:', predictions.length)
            if (predictions.length > 0) {
              setIsOpen(true)
            }
          }}
          onBlur={() => {
            // Delay closing to allow clicking on suggestions
            setTimeout(() => setIsOpen(false), 150)
          }}
          disabled={disabled}
          placeholder={placeholder}
          className={`
            w-full pl-10 pr-3 py-2 border rounded-lg 
            transition-colors focus:outline-none focus:ring-2
            ${error 
              ? 'border-danger-300 bg-danger-50 focus:ring-danger-500' 
              : 'border-secondary-300 focus:ring-primary-500'
            }
            ${disabled ? 'bg-secondary-100 cursor-not-allowed opacity-60' : 'bg-white'}
          `}
        />
      </div>

      {/* Predictions dropdown */}
      {isOpen && predictions.length > 0 && (
        <div 
          className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-300 rounded-lg shadow-xl z-[9999] max-h-64 overflow-y-auto"
          style={{ 
            position: 'absolute',
            zIndex: 9999,
            backgroundColor: 'white',
            border: '1px solid #d1d5db',
            borderRadius: '0.5rem',
            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
            maxHeight: '16rem',
            overflowY: 'auto'
          }}
        >
          {predictions.map((prediction) => (
            <button
              key={prediction.place_id}
              type="button"
              onClick={() => handleSelectPlace(prediction)}
              disabled={isLoading}
              className="
                w-full text-left px-3 py-2 hover:bg-blue-50
                border-b border-gray-100 last:border-b-0
                transition-colors disabled:opacity-50 disabled:cursor-not-allowed
                text-gray-900
              "
              style={{
                width: '100%',
                textAlign: 'left',
                padding: '0.5rem 0.75rem',
                borderBottom: '1px solid #f3f4f6',
                transition: 'background-color 0.15s ease-in-out',
                backgroundColor: 'transparent'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#eff6ff'
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'transparent'
              }}
            >
              <div className="font-medium text-gray-900 text-sm">
                {prediction.main_text || prediction.description}
              </div>
              {prediction.secondary_text && (
                <div className="text-xs text-gray-600">
                  {prediction.secondary_text}
                </div>
              )}
            </button>
          ))}
        </div>
      )}

      {/* Error message */}
      {error && (
        <div className="flex items-center mt-1 text-sm text-danger-600">
          <AlertCircle className="h-4 w-4 mr-1" />
          {error}
        </div>
      )}

      {/* Empty state */}
      {isOpen && predictions.length === 0 && value.length >= 2 && !isLoading && (
        <div 
          className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-300 rounded-lg shadow-xl z-[9999] p-3 text-sm text-gray-600"
          style={{ 
            position: 'absolute',
            zIndex: 9999,
            backgroundColor: 'white',
            border: '1px solid #d1d5db',
            borderRadius: '0.5rem',
            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
          }}
        >
          No results found for "{value}"
        </div>
      )}
    </div>
  )
}
